import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Menu } from "lucide-react";
import { Sidebar } from "@/components/Sidebar";
import { TrialGate } from "@/components/TrialGate";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CookieConsent } from "@/components/CookieConsent";
import { CategoriesProvider } from "@/context/CategoriesContext";
import { UiSettingsProvider } from "@/context/UiSettingsContext";
import { ChatAiProvider } from "@/context/ChatAiContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import InboxPage from "@/pages/InboxPage";
import Condomini from "@/pages/Condomini";
import Pratiche from "@/pages/Pratiche";
import CondominoPortal from "@/pages/CondominoPortal";
import Tickets from "@/pages/Tickets";
import Assistenza from "@/pages/Assistenza";
import Fornitori from "@/pages/Fornitori";
import ChatAI from "@/pages/ChatAI";
import Connetti from "@/pages/Connetti";
import Impostazioni from "@/pages/Impostazioni";
import WorkflowBuilder from "@/pages/WorkflowBuilder";
import PortaliCondomini from "@/pages/PortaliCondomini";
import PaymentResult from "@/pages/PaymentResult";
import ProfiloAccount from "@/pages/ProfiloAccount";
import ConsumiAI from "@/pages/ConsumiAI";
import AcquistiAbbonamenti from "@/pages/AcquistiAbbonamenti";
import RegistroAttivita from "@/pages/RegistroAttivita";
import SedioPartner from "@/pages/SedioPartner";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Legale from "@/pages/Legale";
import RichiediCredenziali from "@/pages/RichiediCredenziali";
import OmiDatasets from "@/pages/OmiDatasets";
import SepaDirectDebit from "@/pages/SepaDirectDebit";
import VisuraSuccess from "@/pages/VisuraSuccess";
import { WelcomeDialog } from "@/components/WelcomeDialog";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento…</div>
);

function AuthCallback() {
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    (async () => {
      if (!sid) { window.location.href = "/login"; return; }
      try {
        await api.post("/auth/session", { session_id: sid });
        window.location.href = "/";
      } catch (e) {
        const denied = e?.response?.status === 403;
        window.location.href = denied ? "/login?denied=1" : "/login?error=1";
      }
    })();
  }, []);
  return <Loading />;
}

function Shell() {
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const [sidebarHoverExpanded, setSidebarHoverExpanded] = useState(false);
  const toggleSidebar = () => setSidebarCollapsed((c) => { const nv = !c; localStorage.setItem("sidebar_collapsed", nv ? "1" : "0"); return nv; });
  // Auto-riduzione della barra nelle sezioni a due colonne (Posta, Gestione condomini) per dare
  // più spazio. All'uscita ripristina la preferenza manuale dell'utente. Non tocca il localStorage.
  const sidebarPrefRef = useRef(sidebarCollapsed);
  const inAutoCollapseRef = useRef(false);
  useEffect(() => {
    const p = location.pathname;
    const auto = p.startsWith("/inbox") || p.startsWith("/condomini");
    if (auto && !inAutoCollapseRef.current) {
      sidebarPrefRef.current = sidebarCollapsed;
      inAutoCollapseRef.current = true;
      setSidebarCollapsed(true);
    } else if (!auto && inAutoCollapseRef.current) {
      inAutoCollapseRef.current = false;
      setSidebarCollapsed(sidebarPrefRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (user && user.onboarding_seen !== true) setWelcomeOpen(true);
  }, [user]);
  // Heartbeat: segnala al backend che il browser è aperto, così l'import/analisi in background
  // di GIORNO gira solo con l'utente collegato (di notte gira comunque). Si ferma a tab chiusa/nascosta.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const beat = () => {
      if (document.visibilityState === "visible") {
        api.post("/client/heartbeat").catch((e) => console.debug("heartbeat", e));
      }
    };
    beat();
    const iv = setInterval(() => { if (alive) beat(); }, 60000);
    const onVis = () => { if (document.visibilityState === "visible") beat(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [user]);
  // Notifica GLOBALE nuove email: un unico poller (12s, scheda visibile) scarica il delta leggero
  // in qualsiasi sezione. Se arrivano email nuove mostra un toast "Nuova email da …" (tranne quando
  // sei già nella Posta) e avvisa l'Inbox di ricaricarsi.
  const pathRef = useRef(location.pathname);
  useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);
  const lastNotifiedMidRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const r = await api.post("/imap/check-new-light");
        if (!alive || !(r.data?.new > 0)) return;
        window.dispatchEvent(new CustomEvent("mailflow:new-mail"));
        if (pathRef.current.startsWith("/inbox")) return; // già nella Posta: niente toast
        const lt = r.data.latest;
        const mid = lt?.message_id || null;
        if (mid && mid === lastNotifiedMidRef.current) return; // stesso messaggio: non ri-notificare
        lastNotifiedMidRef.current = mid;
        const n = r.data.new;
        const who = lt?.from || "una casella";
        if (n === 1) toast.info(`Nuova email da ${who}`, { description: lt?.subject, duration: 6000 });
        else toast.info(`${n} nuove email`, { description: `Ultima da ${who}`, duration: 6000 });
      } catch (e) { console.debug("global new-mail poll", e); }
    };
    const id = setInterval(tick, 12000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  const closeWelcome = async () => {
    setWelcomeOpen(false);
    try { await api.post("/onboarding/seen"); } catch (e) { console.debug("onboarding seen failed", e); }
    setUser((u) => (u ? { ...u, onboarding_seen: true } : u));
  };
  return (
    <CategoriesProvider>
      <UiSettingsProvider>
      <ChatAiProvider>
      <WelcomeDialog open={welcomeOpen} onClose={closeWelcome} />
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 z-20 bg-[#0A0A0A] text-white flex items-center justify-between px-4 border-b border-white/10">
        <button data-testid="mobile-menu-button" onClick={() => setNavOpen(true)} aria-label="Apri menu" className="p-2 -ml-2">
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/sedio-icon.png" alt="Sedio" className="h-7 w-7 rounded-md object-contain" data-testid="mobile-header-logo" />
          <span className="font-head font-extrabold tracking-tight">Sedio</span>
        </div>
        <div className="w-8" />
      </div>
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} hoverExpand={location.pathname.startsWith("/inbox") || location.pathname.startsWith("/condomini")} onHoverExpandChange={setSidebarHoverExpanded} />
      <main className={`${(sidebarCollapsed && !sidebarHoverExpanded) ? "md:ml-[76px]" : "md:ml-64"} min-h-screen pt-14 md:pt-0 transition-[margin] duration-300`}>
        <TrialGate>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/condomini" element={<Condomini />} />
          <Route path="/pratiche" element={<Pratiche />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/fornitori" element={<Fornitori />} />
          <Route path="/chat" element={<ChatAI />} />
          <Route path="/connetti" element={<Connetti />} />
          <Route path="/impostazioni" element={<Impostazioni />} />
          <Route path="/assistenza" element={<Assistenza />} />
          <Route path="/workflow" element={<WorkflowBuilder />} />
          <Route path="/portali-condomini" element={<PortaliCondomini />} />
          <Route path="/payment/success" element={<PaymentResult />} />
          <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
          <Route path="/catasto/visura/success" element={<VisuraSuccess />} />
          <Route path="/catasto/visura/annulla" element={<VisuraSuccess cancelled />} />
          <Route path="/profilo" element={<ProfiloAccount />} />
          <Route path="/consumi" element={<ConsumiAI />} />
          <Route path="/acquisti" element={<AcquistiAbbonamenti />} />
          <Route path="/registro-attivita" element={<RegistroAttivita />} />
          <Route path="/sedio-partner" element={<SedioPartner />} />
          <Route path="/admin/omi" element={<OmiDatasets />} />
          <Route path="/admin/sepa-dd" element={<SepaDirectDebit />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </TrialGate>
      </main>
      </ChatAiProvider>
      </UiSettingsProvider>
    </CategoriesProvider>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

function AppInner() {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/p/:token" element={<Navigate to="/portal" replace />} />
      <Route path="/c/:token" element={<Navigate to="/portal" replace />} />
      <Route path="/portal" element={<CondominoPortal />} />
      <Route path="/richiedi-credenziali/:buildingId" element={<RichiediCredenziali />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/legale" element={<Legale />} />
      <Route path="/verifica-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/*" element={loading ? <Loading /> : user ? <Shell /> : <Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <AppInner />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
      <CookieConsent />
    </div>
    </ThemeProvider>
  );
}

export default App;
